import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, Users, Dog } from "lucide-react";
import { useLang } from "@/i18n";
import { api } from "@/api";
import { SECTION_BY_TYPE } from "@/config";
import ItemCard from "@/components/ItemCard";
import { SectionHead, EmptyState } from "@/components/Widgets";
import WeatherWidget from "@/components/WeatherWidget";

const PRICES = ["€", "€€", "€€€"];

export default function Directory({ type }) {
  const { t } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ categories: [], zones: [] });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const q = searchParams.get("q") || "";
  const category = searchParams.get("categoria") || "";
  const zone = searchParams.get("zona") || "";
  const price = searchParams.get("prezzo") || "";
  const family = searchParams.get("famiglie") === "1";
  const pets = searchParams.get("animali") === "1";

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (type) api.get("/meta/filters", { params: { type } }).then((r) => setFilters(r.data)).catch(() => {});
  }, [type]);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 500 };
    if (type) params.type = type;
    if (q) params.q = q;
    if (category) params.category = category;
    if (zone) params.zone = zone;
    if (price) params.price = price;
    if (family) params.family = true;
    if (pets) params.pets = true;
    api.get("/items", { params })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [type, q, category, zone, price, family, pets]);

  const section = type ? SECTION_BY_TYPE[type] : null;
  const title = section ? t(section.navKey) : (q ? `«${q}»` : t("common.results"));
  const hasPrice = ["restaurant", "accommodation", "activity"].includes(type);

  const pill = (active) =>
    `rounded-full px-4 py-2 text-sm font-semibold transition-colors whitespace-nowrap ${
      active ? "bg-mare text-white" : "bg-white text-grafite hover:bg-sabbia border border-black/5"
    }`;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-testid={`directory-${type || "search"}`}>
      <SectionHead kicker={section ? "Follonica Like" : undefined} title={title} />
      {type === "info" && <WeatherWidget />}

      {/* Search + filter bar */}
      <div className="sticky top-[64px] lg:top-[108px] z-30 -mx-4 px-4 sm:mx-0 sm:px-0 py-2 bg-calce/85 backdrop-blur-md">
        <div className="flex gap-2 items-center">
          <div className="flex-1 flex items-center bg-white rounded-full border border-black/10 px-4 py-2.5 gap-2">
            <Search className="w-4 h-4 text-mare shrink-0" />
            <input
              data-testid="directory-search"
              value={q}
              onChange={(e) => setParam("q", e.target.value)}
              placeholder={t("dir.search")}
              className="flex-1 bg-transparent outline-none text-sm text-grafite placeholder:text-grafite/40"
            />
          </div>
          <button
            data-testid="directory-filter-toggle"
            onClick={() => setShowFilters((v) => !v)}
            className={`rounded-full p-3 transition-colors ${showFilters ? "bg-mare text-white" : "bg-white border border-black/10 text-grafite hover:bg-sabbia"}`}
            aria-label={t("dir.filters")}
          >
            <SlidersHorizontal className="w-4 h-4" />
          </button>
        </div>

        <div className={`mt-3 ${showFilters ? "block" : "hidden"} sm:block`}>
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="filter-categories">
            <button data-testid="filter-cat-all" onClick={() => setParam("categoria", "")} className={pill(!category)}>{t("common.all")}</button>
            {filters.categories.map((c) => (
              <button key={c} data-testid={`filter-cat-${c}`} onClick={() => setParam("categoria", c === category ? "" : c)} className={pill(category === c)}>
                {c}
              </button>
            ))}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 mt-1">
            {filters.zones.length > 1 && filters.zones.map((z) => (
              <button key={z} data-testid={`filter-zone-${z}`} onClick={() => setParam("zona", z === zone ? "" : z)}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors whitespace-nowrap ${zone === z ? "bg-pineta text-white" : "bg-white text-grafite/70 border border-black/10 hover:bg-sabbia"}`}>
                {z}
              </button>
            ))}
            {hasPrice && PRICES.map((p) => (
              <button key={p} data-testid={`filter-price-${p}`} onClick={() => setParam("prezzo", p === price ? "" : p)}
                className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${price === p ? "bg-terracotta text-white" : "bg-white text-grafite/70 border border-black/10 hover:bg-sabbia"}`}>
                {p}
              </button>
            ))}
            <button data-testid="filter-family" onClick={() => setParam("famiglie", family ? "" : "1")}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${family ? "bg-turchese text-white" : "bg-white text-grafite/70 border border-black/10 hover:bg-sabbia"}`}>
              <Users className="w-3.5 h-3.5" /> {t("common.family")}
            </button>
            <button data-testid="filter-pets" onClick={() => setParam("animali", pets ? "" : "1")}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${pets ? "bg-turchese text-white" : "bg-white text-grafite/70 border border-black/10 hover:bg-sabbia"}`}>
              <Dog className="w-3.5 h-3.5" /> {t("common.pets")}
            </button>
          </div>
        </div>
      </div>

      <p className="mt-4 text-sm text-grafite/50" data-testid="results-count">{items.length} {t("common.results")}</p>

      {loading ? (
        <p className="py-20 text-center text-grafite/50">{t("common.loading")}</p>
      ) : items.length === 0 ? (
        <EmptyState text={t("common.empty")} />
      ) : (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item, i) => <ItemCard key={item.slug} item={item} index={i} />)}
        </div>
      )}
    </div>
  );
}
